import type { ActivityViewData, KindConfig } from "./types";
import type { ActivityTrayTab } from "./ui/types";
import { ActivityUIKind } from "./constants";
import { AsyncTaskItem } from "./items/AsyncTaskItem";
import { CIModelChangeItem } from "./items/CIModelChangeItem";
import { InviteItem } from "./items/InviteItem";
import { JoinRequestItem } from "./items/JoinRequestItem";
import { AsyncTaskSource } from "./sources/asyncTaskSource";
import { ActivityEventSource } from "./sources/activityEventSource";
import { InviteSource } from "./sources/inviteSource";
import { JoinRequestSource } from "./sources/joinRequestSource";

/** Single registration point — adding a new activity kind starts here. */
export const ACTIVITY_REGISTRY: Record<ActivityUIKind, KindConfig> = {
    [ActivityUIKind.AsyncTask]: {
        createSource: (ctx) =>
            ctx.workspaceId && ctx.database
                ? new AsyncTaskSource(ctx.workspaceId, ctx.database, ctx.showModal)
                : null,
        Component: AsyncTaskItem
    },
    [ActivityUIKind.CIModelChange]: {
        createSource: (ctx) =>
            ctx.workspaceId && ctx.database
                ? new ActivityEventSource(ctx.workspaceId, ctx.userId, ctx.database, [
                      ActivityUIKind.CIModelChange
                  ])
                : null,
        Component: CIModelChangeItem
    },
    [ActivityUIKind.Invite]: {
        createSource: (ctx) => (ctx.userId ? new InviteSource(ctx.userId) : null),
        Component: InviteItem,
        tab: { id: "invites", label: "Invites", icon: "fa-user-plus" }
    },
    [ActivityUIKind.JoinRequest]: {
        createSource: (ctx) => (ctx.userId ? new JoinRequestSource(ctx.userId) : null),
        Component: JoinRequestItem,
        tab: { id: "invites", label: "Invites", icon: "fa-user-plus" }
    }
};

export function getTabsForKinds(kinds: ActivityUIKind[]): ActivityTrayTab[] {
    const seen = new Set<string>();
    const tabs: ActivityTrayTab[] = [];

    for (const kind of kinds) {
        const config = ACTIVITY_REGISTRY[kind];
        if (!config?.tab) continue;
        if (seen.has(config.tab.id)) continue;
        seen.add(config.tab.id);
        tabs.push(config.tab);
    }
    return tabs;
}

export function getKindsForTab(tabId: string): ActivityUIKind[] {
    return (Object.entries(ACTIVITY_REGISTRY) as [ActivityUIKind, KindConfig][])
        .filter(([, config]) => config.tab?.id === tabId)
        .map(([kind]) => kind);
}

export function filterActivitiesByTab(
    activities: ActivityViewData[],
    tabId: string
): ActivityViewData[] {
    const tabKinds = new Set(getKindsForTab(tabId));
    return activities.filter((a) => tabKinds.has(a.kind));
}
